import { useEffect, useMemo, useState } from "react";
import {
  AudioPair,
  BrewingTransaction,
  Collections,
  PushType,
  User,
} from "../../types/type";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import BrewingTransactionCard from "./BrewingTransactionCard";
import AddSongForm from "../AddSongForm";
import { updateSongRequestToComplete } from "../../api/update.api";
import {
  SendPushNotification,
  sendPushNotification,
} from "../../api/common.api";
import { useDualAudio } from "./hooks/useDualAudio";
import { generateSongId } from "../../api/fetch.api";
import { uploadFileToStorage } from "../../api/storage/storage.api";
import MultiAudioEditor from "../test/MultiAudioEditor";
import { useRecoilState } from "recoil";
import {
  loadedAudioPairState,
  loadedBrewingTransactionState,
} from "../../atom/atom";

type TransactionStatus = BrewingTransaction["status"];
type StatusType = TransactionStatus | "all";

type CompletionModalState = {
  isOpen: boolean;
  transaction: BrewingTransaction | null;
  selectedAudioUrl: string | null;
  initialData?: {
    title: string;
    url: string;
    artistIdList: string[];
    artistName: string;
    isAI: boolean;
    makerUserId: string;
    tag: {
      genreList: string[];
      tagList: string[];
    };
  };
};

const BrewingAdmin: React.FC = () => {
  const [transactions, setTransactions] = useState<BrewingTransaction[]>([]);
  const [selectedAudioPair, setSelectedAudioPair] = useState<
    Record<string, AudioPair>
  >({});

  const [selectedStatus, setSelectedStatus] = useState<StatusType>("pending");
  const [completionModal, setCompletionModal] = useState<CompletionModalState>({
    isOpen: false,
    transaction: null,
    selectedAudioUrl: null,
  });
  const [loadedTransactionIds, setLoadedTransactionIds] = useState<Set<string>>(
    new Set()
  );
  const [loadedAudioPair, setLoadedAudioPair] =
    useRecoilState(loadedAudioPairState);
  const [loadedBrewingTransaction, setLoadedBrewingTransaction] =
    useRecoilState(loadedBrewingTransactionState);

  const {
    currentAudio,
    handlePlayAudioPair,
    handleTimeUpdate,
    handleSkipTime,
    handleTrimAudio,
  } = useDualAudio();

  const [selectedPitches, setSelectedPitches] = useState<
    Record<string, number | null>
  >({});

  useEffect(() => {
    const transactionsQuery = query(
      collection(db, Collections.BREWING_TRANSACTION),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(transactionsQuery, (snapshot) => {
      const newTransactions = snapshot.docs.map((doc) => {
        const transaction = doc.data() as BrewingTransaction;
        return transaction;
      });

      setTransactions(newTransactions);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const newSelectedPitches: Record<string, number | null> = {};

    transactions.forEach((transaction) => {
      // 이미 선택된 피치가 있다면 그대로 유지, 없다면 0으로 설정
      if (!(transaction.transactionId in selectedPitches)) {
        newSelectedPitches[transaction.transactionId] = 0;
      }
    });

    if (Object.keys(newSelectedPitches).length > 0) {
      setSelectedPitches((prev) => ({
        ...prev,
        ...newSelectedPitches,
      }));
    }
  }, [transactions]);

  const handleSelectAudioPair = (
    transactionId: string,
    audioPair: AudioPair
  ) => {
    setSelectedAudioPair((prev) => {
      const newState = { ...prev };
      if (prev[transactionId]?.vocalUrl === audioPair.vocalUrl) {
        // 같은 항목이 선택된 경우 제거
        delete newState[transactionId];
      } else {
        // 다른 항목이 선택된 경우 추가 또는 데이트
        newState[transactionId] = audioPair;
      }
      return newState;
    });
  };

  const handleComplete = async (
    transaction: BrewingTransaction,
    mergedBlob: Blob
  ) => {
    const tempSongId = await generateSongId();
    const audioFile = new File([mergedBlob], `${tempSongId}.mp3`, {
      type: "audio/mp3",
    });
    const storagePath = `/artwork/${tempSongId}.mp3`;
    const storageUrl = await uploadFileToStorage(storagePath, audioFile);

    const userId = transaction.targetUserInfoList[0].userId;
    const userDoc = await getDoc(doc(db, Collections.USER, userId));
    if (!userDoc.exists()) {
      throw new Error(`User with ID ${userId} does not exist`);
    }
    const userData = userDoc.data() as User;
    const userName = userData.userName;

    const initialData = {
      title: transaction.songTitle || "",
      url: storageUrl || "",
      artistIdList: [transaction.existingArtist?.artistId || ""],
      artistName: transaction.existingArtist?.artistName || "",
      isAI: true,
      makerUserId: userName || "Homebrew", // userName으로 변경
      tag: {
        genreList: [],
        tagList: ["homebrewRecommend"],
      },
    };

    setCompletionModal({
      isOpen: true,
      transaction,
      selectedAudioUrl: storageUrl,
      initialData, // initialData 추가
    });
  };

  const handleMergeComplete = async (mergedBlob: Blob) => {
    if (!loadedBrewingTransaction)
      return window.alert("선택된 brewing transaction이 없습니다.");

    // 현재 선택된 피치에 해당하는 audioPair 확인
    const transactionId = loadedBrewingTransaction.transactionId;
    const selectedPitch = selectedPitches[transactionId];

    handleComplete(loadedBrewingTransaction, mergedBlob);
  };

  const handleSongAddSuccess = async (songId: string) => {
    try {
      if (!completionModal.transaction) {
        throw new Error("Transaction data is missing");
      }

      const transaction = completionModal.transaction;
      const transactionRef = doc(
        db,
        Collections.BREWING_TRANSACTION,
        transaction.transactionId
      );

      // songRequest 업데이트
      await updateSongRequestToComplete({
        songRequestId: transaction.songRequestId,
        userId: transaction.targetUserInfoList[0].userId,
        newSongId: songId,
      });

      console.log("updateSongRequestToComplete done !!");

      const userTokenList = transaction.targetUserInfoList
        .map((userInfo) => userInfo.userToken)
        // .filter((token) => typeof token === "string" && Boolean);
        .filter((token): token is string => Boolean(token));

      console.log("userTokenList : ", userTokenList);

      let notificationResult: SendPushNotification = {
        successCount: 0,
        failureCount: 0,
        successfulTokenList: [],
        failedTokenList: [],
      };

      if (userTokenList.length > 0) {
        const notificationBody = {
          title: "제조 완료",
          body: "내 곡이 만들어졌어요. 지금 바로 들어보세요!",
          tokenList: userTokenList,
          data: {
            songTitle: transaction.songTitle,
            screen: "MyBrewingList" as const,
            type: "제조완료" as PushType,
            songId: songId,
          },
        };

        notificationResult = await sendPushNotification(notificationBody);
      }

      const notificationsSent: BrewingTransaction["notificationsSent"] =
        transaction.targetUserInfoList.map((userInfo) => {
          // 기본 객체 생성
          const notification = {
            userId: userInfo.userId,
            status:
              userInfo.userToken &&
              notificationResult.successfulTokenList.includes(
                userInfo.userToken
              )
                ? ("completed" as const)
                : ("failed" as const),
          };

          // error가 있 때만 추가
          const error = userInfo.userToken
            ? notificationResult.failedTokenList.includes(userInfo.userToken)
              ? "Failed to send notification"
              : null // undefined 대신 null 사용
            : "No user token available";

          // error가 null이 아닐 만 error 필드 추가
          if (error !== null) {
            return { ...notification, error };
          }

          return notification;
        });

      const brewingTransactionUpdate = {
        status: "completed",
        completedAt: serverTimestamp(),
        songId: songId,
        notificationsSent,
        updatedUserIdList: transaction.targetUserInfoList.map(
          (userInfo) => userInfo.userId
        ),
      };

      console.log("brewingTransactionUpdate : ", brewingTransactionUpdate);

      // brewingTransaction 업데이트
      await updateDoc(transactionRef, brewingTransactionUpdate);

      // 클라이언트 업데이트
      setTransactions((prev) =>
        prev.map((t) =>
          t.transactionId === transaction.transactionId
            ? { ...t, status: "completed", songId: songId }
            : t
        )
      );

      // 5. Close modal and reset completion modal state
      setCompletionModal({
        isOpen: false,
        transaction: null,
        selectedAudioUrl: null,
      });
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error details:", {
          message: error.message,
          name: error.name,
          stack: error.stack,
        });
      } else {
        console.error("Unknown error:", error);
      }
      alert("Failed to complete the song addition process");
    }
  };

  const filteredTransactions = useMemo(() => {
    const filtered = transactions.filter(
      (transaction) =>
        selectedStatus === "all" || transaction.status === selectedStatus
    );

    // 상위 3개 transaction의 ID를 새로운 Set으로 생성
    const topThreeIds = new Set(
      filtered.slice(0, 1).map((t) => t.transactionId)
    );

    // loadedTransactionIds 업데이트
    setLoadedTransactionIds(topThreeIds);

    return filtered;
  }, [transactions, selectedStatus]);

  const handleSelectPitch = (transactionId: string, pitch: number | null) => {
    setSelectedPitches((prev) => {
      const newState = { ...prev };

      // 현재 선택된 pitch와 같은 값을 다시 선택하면 null로 설정 (All로 돌아감)
      if (prev[transactionId] === pitch) {
        newState[transactionId] = null;
      } else {
        // 다른 pitch를 선택하면 해당 값으로 설정
        newState[transactionId] = pitch;
      }

      return newState;
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.innerContainer}>
        <h1 style={styles.title}>신청곡 제조 자동화</h1>

        <StatusFiler
          selectedStatus={selectedStatus}
          onSelectStatus={setSelectedStatus}
        />
        <div style={styles.grid}>
          {filteredTransactions.map((transaction) => (
            <BrewingTransactionCard
              key={transaction.transactionId}
              transaction={transaction}
              shouldLoadAudio={loadedTransactionIds.has(
                transaction.transactionId
              )}
              currentAudioState={currentAudio}
              onPlayAudioPair={handlePlayAudioPair}
              onSkipTime={handleSkipTime}
              onTimeUpdate={handleTimeUpdate}
              selectedAudioPair={selectedAudioPair}
              onSelectAudioPair={handleSelectAudioPair}
              onTrimAudio={handleTrimAudio}
              selectedPitch={selectedPitches[transaction.transactionId] ?? null}
              onSelectPitch={(pitch) =>
                handleSelectPitch(transaction.transactionId, pitch)
              }
            />
          ))}
        </div>
        {completionModal.isOpen &&
          completionModal.transaction &&
          completionModal.initialData && (
            <AddSongForm
              initialData={completionModal.initialData}
              isOpen={completionModal.isOpen}
              onClose={() =>
                setCompletionModal({
                  isOpen: false,
                  transaction: null,
                  selectedAudioUrl: null,
                  initialData: undefined,
                })
              }
              onSuccess={handleSongAddSuccess}
            />
          )}
      </div>
      {loadedAudioPair && (
        <div style={styles.editorWrapper}>
          <MultiAudioEditor
            audioUrl={loadedAudioPair.vocalUrl}
            audioUrl2={loadedAudioPair.mrUrl}
            onClose={() => {
              setLoadedAudioPair(null);
              setLoadedBrewingTransaction(null);
            }}
            onMergeComplete={handleMergeComplete}
          />
        </div>
      )}
    </div>
  );
};

type StatusFilter = {
  selectedStatus: StatusType;
  onSelectStatus: (status: StatusType) => void;
};
const StatusFiler: React.FC<StatusFilter> = ({
  selectedStatus,
  onSelectStatus,
}) => {
  const statusConfigs = {
    all: {
      label: "All",
      activeStyle: { backgroundColor: "#6B7280", color: "white" },
      defaultStyle: { backgroundColor: "#F3F4F6", color: "#6B7280" },
    },
    completed: {
      label: "Completed",
      activeStyle: { backgroundColor: "#10B981", color: "white" },
      defaultStyle: { backgroundColor: "#D1FAE5", color: "#059669" },
    },
    pending: {
      label: "Pending",
      activeStyle: { backgroundColor: "#F59E0B", color: "white" },
      defaultStyle: { backgroundColor: "#FEF3C7", color: "#D97706" },
    },
    failed: {
      label: "Failed",
      activeStyle: { backgroundColor: "#EF4444", color: "white" },
      defaultStyle: { backgroundColor: "#FEE2E2", color: "#DC2626" },
    },
  } as const;

  return (
    <div style={styles.filterContainer}>
      <>
        {(Object.keys(statusConfigs) as StatusType[]).map((status) => (
          <button
            key={status}
            onClick={() => onSelectStatus(status)}
            style={{
              ...styles.filterButton,
              ...(selectedStatus === status
                ? statusConfigs[status].activeStyle
                : statusConfigs[status].defaultStyle),
            }}
          >
            {statusConfigs[status].label}
          </button>
        ))}
      </>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#F9FAFB",
    padding: "32px",
  },
  innerContainer: {
    maxWidth: "1280px",
    margin: "0 auto",
  },
  title: {
    fontSize: "30px",
    fontWeight: "700",
    color: "#111827",
    marginBottom: "32px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
    gap: "16px",
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  },
  cardHeader: {
    padding: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #F3F4F6",
  },
  headerInfo: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  transactionId: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
  },
  basicInfo: {
    padding: "16px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  infoLabel: {
    fontSize: "12px",
    color: "#6B7280",
    marginBottom: "4px",
  },
  infoValue: {
    fontSize: "14px",
    color: "#111827",
    fontWeight: "500",
  },
  expandedContent: {
    padding: "16px",
    borderTop: "1px solid #F3F4F6",
  },
  sectionTitle: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6B7280",
    marginBottom: "8px",
  },
  audioContainer: {
    backgroundColor: "#F9FAFB",
    padding: "12px",
    borderRadius: "6px",
    marginBottom: "8px",
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  buttonGroup: {
    display: "flex",
    gap: "4px",
  },
  controlButton: {
    background: "none",
    border: "none",
    padding: "6px",
    cursor: "pointer",
    color: "#6B7280",
  },
  controlButtonDisabled: {
    opacity: "0.5",
    cursor: "not-allowed",
  },
  progressBar: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
  },
  timeText: {
    fontSize: "12px",
    color: "#6B7280",
    minWidth: "40px",
  },
  slider: {
    flex: 1,
    height: "4px",
    borderRadius: "2px",
    backgroundColor: "#E5E7EB",
    WebkitAppearance: "none" as "none",
  },
  targetUsersList: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    marginTop: "8px",
  },
  userItem: {
    fontSize: "12px",
    color: "#4B5563",
    backgroundColor: "#F9FAFB",
    padding: "8px 12px",
    borderRadius: "4px",
  },
  errorMessage: {
    backgroundColor: "#FEF2F2",
    color: "#DC2626",
    padding: "12px",
    borderRadius: "6px",
    fontSize: "12px",
    marginTop: "16px",
  },
  filterContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "24px",
  },

  filterButton: {
    padding: "8px 16px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500" as const,
    cursor: "pointer",
    transition: "all 0.2s",
    outline: "none",
  },
  editorWrapper: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    backgroundColor: "#1a1a1a",
    boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
    overflow: "auto",
  },
};

export default BrewingAdmin;
